import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menubar } from 'primereact/menubar';
import { SignedIn, SignedOut, UserButton } from '@clerk/clerk-react';
import { jwtDecode } from 'jwt-decode';
import 'primereact/resources/themes/lara-dark-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './Header.css';

import useOrgJwt from '../hooks/useOrgJwt';

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { role: hookRole } = useOrgJwt();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menubarRef = useRef(null);

  const derivedRole = useMemo(() => {
    if (hookRole) return String(hookRole).toLowerCase();
    try {
      const raw = localStorage.getItem('org_jwt');
      if (!raw) return 'viewer';
      const decoded = jwtDecode(raw);
      return String(decoded.role || decoded.org_role || decoded.organization_role || 'viewer').toLowerCase();
    } catch {
      return 'viewer';
    }
  }, [hookRole]);

  // Close mobile menu when clicking outside
  const handleClickOutside = useCallback((event) => {
    if (menubarRef.current && !menubarRef.current.contains(event.target)) {
      const menuButton = menubarRef.current.querySelector('.p-menubar-button');
      const rootList = menubarRef.current.querySelector('.p-menubar-root-list');
      if (rootList && window.getComputedStyle(rootList).display !== 'none') {
        menuButton?.click();
        setMobileMenuOpen(false);
      }
    }
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    const menuButton = menubarRef.current?.querySelector('.p-menubar-button');
    const rootList = menubarRef.current?.querySelector('.p-menubar-root-list');
    if (rootList && window.getComputedStyle(rootList).display !== 'none') {
      menuButton?.click();
    }
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Add/remove click outside listener
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [handleClickOutside]);

  const isOn = (path) => location.pathname === path;
  const inApp = location.pathname.startsWith('/iqledger');

  // Wrap navigation to close mobile menu
  const navTo = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const brand = (
    <div className="nav-brand" onClick={() => navTo('/')}>
      <img alt="logo" src="/iqtechnologies_logo.png" className="nav-logo" />
      <span className="brand-text">IQTechnologies</span>
    </div>
  );

  const commonItems = [
    { label: 'Home',    command: () => navTo('/'),        className: isOn('/') ? 'active-header' : '' },
    { label: 'Docs',    command: () => navTo('/docs'),    className: isOn('/docs') ? 'active-header' : '' },
    { label: 'Blog',    command: () => navTo('/blog'),    className: isOn('/blog') ? 'active-header' : '' },
    { label: 'Contact', command: () => navTo('/contact'), className: isOn('/contact') ? 'active-header' : '' }
  ];

  const appItems = [
    { label: 'IQLedger', command: () => navTo('/iqledger'), className: inApp ? 'active-header' : '' },
    { template: () => (<div className="user-button-wrapper"><UserButton afterSignOutUrl="/signed-out" /></div>) }
  ];

  // Unauth'd CTA goes to PRICING (plan first)
  const unauthedItems = [
    { label: 'Log In',  command: () => navTo('/sign-in') },
    { label: 'Sign Up', command: () => navTo('/pricing') }
  ];

  return (
    <header className="nav-wrapper" ref={menubarRef}>
      {mobileMenuOpen && <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} />}
      <SignedIn>
        <Menubar 
          model={[...commonItems, ...appItems]} 
          start={brand} 
          className="custom-menubar"
          onShow={() => setMobileMenuOpen(true)}
          onHide={() => setMobileMenuOpen(false)}
        />
      </SignedIn>
      <SignedOut>
        <Menubar 
          model={[...commonItems, ...unauthedItems]} 
          start={brand} 
          className="custom-menubar"
          onShow={() => setMobileMenuOpen(true)}
          onHide={() => setMobileMenuOpen(false)}
        />
      </SignedOut>
    </header>
  );
}

export default Header;
